import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, Recycle, Ruler, Sparkles } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES, CONTACT } from "@/lib/content";

const STATS = [
  { k: "100+", v: "Lawns maintained" },
  { k: "2 yrs", v: "On the tools" },
  { k: "100%", v: "Satisfaction guarantee" },
  { k: "0", v: "Waste to landfill goal" },
];

const STANDARDS = [
  { icon: Ruler, title: "Brand new equipment", body: "Modern mowers, edgers and battery tools for a clean, quiet, consistent finish every visit." },
  { icon: Recycle, title: "Eco-conscious waste disposal", body: "All green waste is collected and composted or recycled responsibly — never dumped." },
  { icon: ShieldCheck, title: "Reliable & trustworthy", body: "Turn up on time, every time. Honest, dependable service for total peace of mind on your property." },
  { icon: Sparkles, title: "Invested in the finer details", body: "Edges, borders and finish lines get our full attention. The details are the whole job." },
];

export default function About({ onQuote }) {
  const navigate = useNavigate();
  return (
    <main className="overflow-x-hidden">
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <button data-testid="about-back" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-[#52635A] hover:text-[#0B0F0C] transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Back home
          </button>
          <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B]">About Us</p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-4 items-end">
            <h1 data-testid="about-heading" className="lg:col-span-8 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
              A small crew with an obsessive standard for green spaces.
            </h1>
            <p className="lg:col-span-4 text-lg text-[#52635A] leading-relaxed">
              CBA Mowing started with a group of four friends who wanted to work hard to bring amazing environmental work to the community. We've kept that spirit and grown around it.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="rounded-[28px] overflow-hidden">
              <img src={IMAGES.about} alt="The CBA Mowing team" className="w-full h-[320px] sm:h-[480px] object-cover object-[50%_68%]" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.v} delay={i * 0.08}>
              <div className="border-t-2 border-[#0B0F0C] pt-5">
                <p className="font-display font-extrabold text-4xl sm:text-5xl">{s.k}</p>
                <p className="mt-2 text-sm text-[#52635A]">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-20 md:pb-28 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start mb-14">
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">Our craft standards</h2>
              <p className="text-[#52635A] leading-relaxed text-lg">
                Anyone can cut grass. We build a repeatable, professional finish through the right gear, respect for your property and an eye for the details other crews skip.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {STANDARDS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="h-full bg-[#F4F7F5] rounded-3xl p-8 border border-black/5">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#0B0F0C]">
                    <s.icon className="w-5 h-5 text-[#00E676]" />
                  </span>
                  <h3 className="mt-6 font-display font-bold text-xl">{s.title}</h3>
                  <p className="mt-3 text-[#52635A] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 px-4 sm:px-6 lg:px-12">
        <Reveal>
          <div className="max-w-7xl mx-auto rounded-[32px] bg-[#0B0F0C] text-white px-8 sm:px-14 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">Ready to see the difference?</h2>
              <p className="mt-4 text-[#9EB0A5]">Free quotes · {CONTACT.hours} · {CONTACT.phone}</p>
            </div>
            <button data-testid="about-cta-quote" onClick={() => onQuote()} className="inline-flex items-center gap-2 rounded-full bg-[#00E676] text-[#0B0F0C] font-semibold px-8 py-4 hover:bg-white transition-colors duration-300 shrink-0">
              Get a Free Quote <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
